import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { Notepad } from "@phosphor-icons/react";
import { DOCUMENT_TYPE_GROUPS, DOCUMENT_TYPE_LABELS, type DocumentType } from "@/types";
import type { DocumentFormSubmit } from "./types";

function slugify(title: string): string {
  return title
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export interface DocumentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: DocumentFormSubmit) => void;
  isPending: boolean;
}

export function DocumentFormDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: DocumentFormDialogProps) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [type, setType] = useState<DocumentType>("ostalo");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [hasSignatureBlock, setHasSignatureBlock] = useState(false);

  const onClose = () => onOpenChange(false);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugTouched) setSlug(slugify(value));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      title,
      slug,
      type,
      description,
      isActive,
      hasSignatureBlock,
    });
  };

  if (!open) return null;

  return (
    <Dialog open onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogHeader onClose={onClose}>
          <AdminDialogHeading
            icon={Notepad}
            title="Novi dokument"
            description="Kreirajte novi ugovor, saglasnost ili pravni akt."
          />
        </DialogHeader>

        <DialogBody>
          <div className="flex flex-col gap-5">
            <div className="space-y-2">
              <Label htmlFor="doc-title">Naziv</Label>
              <Input
                id="doc-title"
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="npr. Ugovor sa terapeutom"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-slug">Slug</Label>
              <Input
                id="doc-slug"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
                placeholder="npr. ugovor-sa-terapeutom"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-type">Tip</Label>
              <Select value={type} onValueChange={(v) => setType(v as DocumentType)}>
                <SelectTrigger id="doc-type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPE_GROUPS.map((group) => (
                    <SelectGroup key={group.label}>
                      <SelectLabel>{group.label}</SelectLabel>
                      {group.types.map((t) => (
                        <SelectItem key={t} value={t}>
                          {DOCUMENT_TYPE_LABELS[t]}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-description">Opis</Label>
              <Textarea
                id="doc-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Kratak opis namjene dokumenta…"
              />
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="doc-active"
                checked={isActive}
                onCheckedChange={(v) => setIsActive(v === true)}
                className="mt-1"
              />
              <div>
                <Label htmlFor="doc-active" className="font-medium">
                  Aktivan
                </Label>
                <p className="text-xs text-muted-foreground">
                  Aktivan dokument se koristi kao važeća verzija za svoj tip.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="doc-signature"
                checked={hasSignatureBlock}
                onCheckedChange={(v) => setHasSignatureBlock(v === true)}
                className="mt-1"
              />
              <div>
                <Label htmlFor="doc-signature" className="font-medium">
                  Prikaži potpisni blok
                </Label>
                <p className="text-xs text-muted-foreground">
                  Dodaje prostor za potpis na kraju generiranog PDF-a.
                </p>
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Otkaži
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? "Kreiranje..." : "Kreiraj dokument"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
